import { Context } from "vm";
import { GedeeltelijkeUitkeringAoOpties, AovSoortProductOptions } from "../../.generated/forms/formstypes";
import { createISWSideEffect, initISWSideEffect } from "../../shared/components/isw-side-effects/create-isw-helpers";
import { AoUitkeringswijze, SpecificatieAoUitkeringModalType } from "../infra/specificatie-ao-uitkering-schema";
import { getDefaultIndexeringen, wgaDefaultMinPercentage, wgaDefaultTotPercentage } from "./indexering-helper";

const indexeringenProRataStaffelSideEffects = createISWSideEffect<SpecificatieAoUitkeringModalType, Context>(
  ({ draft, context }) => {
    if (!context.touchedIndexeringen) return;
    for (let i = 0; i < draft.indexeringen.length; i++) {
      const touched = context.touchedIndexeringen[i] || {};
      if (
        touched.ondergrensPercentage === true ||
        touched.bovengrensPercentage === true ||
        touched.uitkeringPercentage === true
      ) {
        touched.ondergrensPercentage = false;
        touched.bovengrensPercentage = false;
        touched.uitkeringPercentage = false;
      }
    }
    const length = draft.indexeringen.length;
    for (let i = 0; i < length; i++) {
      const currIndexering = draft.indexeringen[i];
      const nextIndexering = i + 1 < length ? draft.indexeringen[i + 1] : null;
      currIndexering.error = false;
      if (
        (nextIndexering && currIndexering.bovengrensPercentage !== nextIndexering.ondergrensPercentage) ||
        draft.indexeringen[length - 1].bovengrensPercentage !== 100
      ) {
        currIndexering.error = true;
      }

      if (
        context.selectedProduct?.soortProduct === AovSoortProductOptions.WgaGatVerzekering &&
        i === 0 &&
        draft.indexeringen[0].bovengrensPercentage &&
        draft.indexeringen[0].uitkeringPercentage === 0
      ) {
        // Bij de WGA-gatverzekering "Minimaal percentage arbeidsongeschiktheid" syncen met 'percentage tot' van de eerste staffelregel
        draft.proRataMinPercentage = draft.indexeringen[0].bovengrensPercentage;
      } else if (
        context.selectedProduct?.soortProduct === AovSoortProductOptions.WgaGatVerzekering &&
        i === length - 1 &&
        draft.indexeringen[length - 1].ondergrensPercentage &&
        draft.indexeringen[length - 1].uitkeringPercentage === 0
      ) {
        // Bij de WGA-gatverzekering "Geen uitkering vanaf" syncen met 'percentage vanaf' van de laatste staffelregel
        draft.proRataTotPercentage = draft.indexeringen[length - 1].ondergrensPercentage;
      }
    }
  }
);

const indexeringenProRataStaffelDefaultSideEffects = createISWSideEffect<SpecificatieAoUitkeringModalType, Context>(
  ({ draft, context }) => {
    draft.indexeringen = getDefaultIndexeringen(
      context.aoUitkeringswijze,
      draft,
      context.selectedProduct?.soortProduct
    );
  }
);

const syncVolledigPercentageAndIndexeringen = (draft: SpecificatieAoUitkeringModalType): void => {
  draft.volledigVanafPercentage = draft.volledigMinPercentage;

  // Geef de eerste bovengrensPercentage in indexeringen dezelfde waarde
  if (Array.isArray(draft.indexeringen) && draft.indexeringen.length > 0) {
    draft.indexeringen[0].bovengrensPercentage = draft.volledigMinPercentage;
    if (draft.indexeringen.length > 1) {
      // Geef de tweede ondergrensPercentage in indexeringen dezelfde waarde
      draft.indexeringen[1].ondergrensPercentage = draft.volledigMinPercentage;
    }
  }
};

const specificatieAoUitkeringModalSideEffects = createISWSideEffect<SpecificatieAoUitkeringModalType, Context>(bag => {
  const { context, has, draft } = bag;

  if (
    bag.context.selectedProduct?.soortProduct === AovSoortProductOptions.WgaGatVerzekering &&
    bag.context.aoUitkeringswijze === AoUitkeringswijze.ProRata &&
    has.gedeeltelijkeUitkeringObv.changed &&
    bag.draft.gedeeltelijkeUitkeringObv === GedeeltelijkeUitkeringAoOpties.WerkelijkAoPercentage
  ) {
    draft.proRataMinPercentage = wgaDefaultMinPercentage;
    draft.proRataTotPercentage = wgaDefaultTotPercentage;
  }

  if (
    context.aoUitkeringswijze === AoUitkeringswijze.ProRata &&
    (has.gedeeltelijkeUitkeringObv.changed || has.proRataMinPercentage.changed || has.proRataTotPercentage.changed)
  ) {
    indexeringenProRataStaffelDefaultSideEffects(bag);
  }

  if (
    bag.context.aoUitkeringswijze === AoUitkeringswijze.ProRata &&
    bag.draft.gedeeltelijkeUitkeringObv === GedeeltelijkeUitkeringAoOpties.Staffel
  ) {
    indexeringenProRataStaffelSideEffects(bag);
    return;
  }

  if (
    context.aoUitkeringswijze === AoUitkeringswijze.Volledig &&
    draft.volledigMinPercentage !== draft.volledigVanafPercentage
  ) {
    syncVolledigPercentageAndIndexeringen(draft);
  }
});

export const determineSpecificatieAoUitkeringModalSideEffects = initISWSideEffect(
  specificatieAoUitkeringModalSideEffects
);
